import { FormEvent, useState } from 'react';
import { Lock, User, AlertCircle } from 'lucide-react';
import { useAuthStore } from './authStore';
import { cn } from '@/lib/cn';

export function LoginPage() {
  const signIn = useAuthStore((s) => s.signIn);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = signIn(username, password);
    if (!ok) {
      setError('Invalid username or password.');
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background text-foreground">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.18] dark:opacity-[0.22]"
        style={{ background: 'var(--brand-gradient)' }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 0%, var(--background) 75%)',
        }}
      />

      <div className="relative w-full max-w-sm px-6">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img
            src={`${import.meta.env.BASE_URL}branding/gto-icon.png`}
            alt="GTO"
            className="size-20 rounded-lg object-contain shadow-sm"
            draggable={false}
          />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">GTO Agentic GIS</h1>
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              by Geotech Overseas
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-border bg-card p-6 shadow-sm"
          autoComplete="off"
        >
          <div className="mb-4">
            <h2 className="text-sm font-medium">Sign in to continue</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Internal demo access only.
            </p>
          </div>

          <label className="mb-3 block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Username
            </span>
            <div className="relative">
              <User className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (error) setError(null);
                }}
                autoFocus
                autoComplete="username"
                className="block w-full rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring"
                placeholder="Enter username"
                required
              />
            </div>
          </label>

          <label className="mb-4 block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Password
            </span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                autoComplete="current-password"
                className="block w-full rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring"
                placeholder="Enter password"
                required
              />
            </div>
          </label>

          {error && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors',
              'hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            Sign in
          </button>
        </form>

        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          You'll be signed out automatically when this session ends.
        </p>
      </div>
    </div>
  );
}
